import { useContext, useEffect, useMemo, useState } from 'react';

import { Button, useDisclosure } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import { DealStateEnum } from '../../../../gql/dealGql';
import {
  DealMediaTypeEnum,
  ThirdPartyPayoffSendStatus,
  useLatestThirdPartyPayoffSendQuery,
  useSendThirdPartyPayoffMutation,
} from '../../../../gql/generated/graphql';

import Modal from '../../../shared/Modal';
import Tooltip from '../../../shared/Tooltip';

import { LDFlags } from '../../../../constants/experiments';
import { PermissionEnum } from '../../../../constants/permissions';
import useFlag from '../../../../hooks/useFlag';
import { useLienholderThirdPartyPayoffs } from '../../../../hooks/useLienholderThirdPartyPayoffs';
import { DealContext } from '../../../../libs/DealContext';
import { logger } from '../../../../libs/Logger';
import { AbilityContext } from '../../../../libs/contextLib';
import { formatDateAndTime } from '../../../../utils/dates';
import { dealMediaToFile } from '../../../../utils/media';
import DocumentsTable from '../../../DocumentsTable/DocumentsTable';

const SendPayoffToFirstFedButton = () => {
  const { deal, documentMedia } = useContext(DealContext);
  const abilities = useContext(AbilityContext);

  const firstFedEnabled = !!useFlag(LDFlags.FIRST_FEDERAL_PAYOFFS);

  const canSend =
    firstFedEnabled &&
    abilities.has(PermissionEnum.SendThirdPartyPayoff) &&
    deal.state === DealStateEnum.SendPayoff;
  const isThirdPartyPayoff = useLienholderThirdPartyPayoffs({ deal, skip: !canSend });

  const payoffPacketFiles = useMemo(
    () =>
      documentMedia.mediaList.flatMap((mediaItem) =>
        mediaItem.type === DealMediaTypeEnum.PayoffPacket && mediaItem.key
          ? [dealMediaToFile(mediaItem)]
          : [],
      ),
    [documentMedia.mediaList],
  );
  const noPayoffPackets = payoffPacketFiles.length === 0;

  const { data: latestSendData, refetch: refetchLatestSend } = useLatestThirdPartyPayoffSendQuery({
    variables: { dealId: deal.id ?? 0 },
    skip: !canSend || !isThirdPartyPayoff || deal.id == null,
    fetchPolicy: 'cache-and-network',
  });
  const latestSend = latestSendData?.latestThirdPartyPayoffSend;
  const sentAt =
    latestSend?.status === ThirdPartyPayoffSendStatus.Sent ? latestSend.created_at : undefined;

  const [sendThirdPartyPayoff, { loading }] = useSendThirdPartyPayoffMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFilesIndexes, setSelectedFilesIndexes] = useState<number[]>([]);

  // Selections are row indexes, so a refreshed media list would point them at other files.
  useEffect(() => {
    setSelectedFilesIndexes([]);
  }, [payoffPacketFiles]);

  const handleCloseModal = () => {
    setSelectedFilesIndexes([]);
    onClose();
  };

  const handleSend = async () => {
    if (deal.id == null) {
      return;
    }

    const keys = selectedFilesIndexes.flatMap((index) => payoffPacketFiles[index]?.key ?? []);

    try {
      await sendThirdPartyPayoff({ variables: { dealId: deal.id, keys } });
      toast.success('Payoff sent to First Fed');
      handleCloseModal();
    } catch (e) {
      // The modal stays open so a failed send can be retried.
      logger.error('SendPayoffToFirstFedButton.tsx', 'handleSend', { dealId: deal.id }, e);
      toast.error(
        `Failed to send the payoff to First Fed: ${
          e instanceof Error ? e.message : 'Unknown error'
        }`,
      );
    } finally {
      // A refused send may mean another attempt already went, so the button can show it.
      refetchLatestSend().catch(() => undefined);
    }
  };

  if (!canSend || !isThirdPartyPayoff) {
    return null;
  }

  if (sentAt) {
    return (
      <Tooltip
        label={`Sent to First Fed on ${formatDateAndTime(sentAt)}`}
        bgColor="gray.700"
        placement="top"
      >
        <Button name="sent-to-first-fed" variant="secondary" disabled>
          SENT TO FIRST FED
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip errors={noPayoffPackets ? ['No Payoff Packet to send'] : undefined}>
        <Button
          name="open-send-payoff-to-first-fed-modal"
          variant="secondary"
          onClick={onOpen}
          disabled={noPayoffPackets}
        >
          SEND TO FIRST FED
        </Button>
      </Tooltip>
      <Modal
        title="Send Payoff to First Fed"
        isOpen={isOpen}
        onClose={handleCloseModal}
        canDismiss={!loading}
        closeOnOverlayClick={false}
        size="3xl"
        leftButtons={
          <Button variant="warning" onClick={handleCloseModal} isDisabled={loading}>
            CANCEL
          </Button>
        }
        rightButtons={
          <Button
            name="send-payoff-to-first-fed"
            isLoading={loading}
            loadingText="SEND"
            isDisabled={!selectedFilesIndexes.length}
            onClick={handleSend}
          >
            SEND
          </Button>
        }
      >
        <DocumentsTable
          infoText="Select the payoff packet to send to First Fed."
          files={payoffPacketFiles}
          selectedFilesIndexes={selectedFilesIndexes}
          setSelectedFilesIndexes={setSelectedFilesIndexes}
          modal="upload"
        />
      </Modal>
    </>
  );
};

export default SendPayoffToFirstFedButton;
